/**
 * Per-kind drill/solve/results cards and the info card, loaded lazily.
 *
 * Every loader is an explicit `import()` literal so the bundler emits the same
 * per-kind chunks as the old template-literal imports; each async component is
 * created once per kind/slot and cached, so a recompute never remounts a card.
 */
import {
  computed,
  defineAsyncComponent,
  toValue,
  type Component,
  type MaybeRefOrGetter,
} from 'vue';
import type { StepTypeCamelCase } from 'src/types/course/step';
import type { DrillKind } from '../utilities/drillStrategies/types';

type DrillSlot = 'drill' | 'solve' | 'results';
type Loader = () => Promise<unknown>;
export type PrefetchSlot = DrillSlot | 'info';

/** Exported only so tests can spy on the loaders; not part of the public API. */
export const LOADERS: { [K in DrillKind]: Record<DrillSlot, Loader> } = {
  Vocabulary: {
    drill: () =>
      import('../components/Vocabulary/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/Vocabulary/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/Vocabulary/StepResultsCard/StepResultsCard.vue'),
  },
  Pairs: {
    drill: () => import('../components/Pairs/StepDrillCard/StepDrillCard.vue'),
    solve: () => import('../components/Pairs/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/Pairs/StepResultsCard/StepResultsCard.vue'),
  },
  SingleChoice: {
    drill: () =>
      import('../components/SingleChoice/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/SingleChoice/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/SingleChoice/StepResultsCard/StepResultsCard.vue'),
  },
  MultiChoice: {
    drill: () =>
      import('../components/MultiChoice/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/MultiChoice/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/MultiChoice/StepResultsCard/StepResultsCard.vue'),
  },
  FillInTheBlank: {
    drill: () =>
      import('../components/FillInTheBlank/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/FillInTheBlank/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/FillInTheBlank/StepResultsCard/StepResultsCard.vue'),
  },
  CrossMatch: {
    drill: () =>
      import('../components/CrossMatch/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/CrossMatch/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/CrossMatch/StepResultsCard/StepResultsCard.vue'),
  },
  // The question card is Vocabulary's: the question is the same.
  TrueFalse: {
    drill: () =>
      import('../components/Vocabulary/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/TrueFalse/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/TrueFalse/StepResultsCard/StepResultsCard.vue'),
  },
  // Question and results are Vocabulary's: same question, same text answers.
  WordBuilder: {
    drill: () =>
      import('../components/Vocabulary/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/WordBuilder/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/Vocabulary/StepResultsCard/StepResultsCard.vue'),
  },
  // Question and results are Vocabulary's: same question, same text answers.
  MemoryFlip: {
    drill: () =>
      import('../components/Vocabulary/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/MemoryFlip/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/Vocabulary/StepResultsCard/StepResultsCard.vue'),
  },
  SentenceBuilder: {
    drill: () =>
      import('../components/SentenceBuilder/StepDrillCard/StepDrillCard.vue'),
    solve: () =>
      import('../components/SentenceBuilder/StepSolveCard/StepSolveCard.vue'),
    results: () =>
      import('../components/SentenceBuilder/StepResultsCard/StepResultsCard.vue'),
  },
};

/** Exported only so tests can spy on the loaders; not part of the public API. */
export const INFO_LOADERS: Record<'Theory' | 'default', Loader> = {
  Theory: () => import('../components/Theory/StepInfoCard/StepInfoCard.vue'),
  default: () => import('../components/StepInfoCard/StepInfoCard.vue'),
};

const cache = new Map<string, Component>();

function cached(key: string, loader: Loader): Component {
  let component = cache.get(key);
  if (!component) {
    component = defineAsyncComponent(
      loader as Parameters<typeof defineAsyncComponent>[0],
    );
    cache.set(key, component);
  }
  return component;
}

const prefetched = new Map<string, Promise<void>>();

function prefetchOnce(key: string, loader: Loader): Promise<void> {
  let pending = prefetched.get(key);
  if (!pending) {
    pending = Promise.resolve()
      .then(loader)
      .then(
        () => undefined,
        // A failed prefetch must never surface; forget it so a later prefetch
        // (or the real defineAsyncComponent load, which handles errors) retries.
        () => {
          prefetched.delete(key);
        },
      );
    prefetched.set(key, pending);
  }
  return pending;
}

/**
 * Warms the lazy card chunks for a step kind WITHOUT creating components:
 * calls the same `import()` loaders the cards use, so the same chunks are
 * fetched and the bundler's module cache dedups the later real load.
 * Idempotent per kind/slot and never rejects. 'Theory' has only the info
 * slot (its own card); 'Filter' only the default info card (pass the resolved
 * drill kind to warm its cards); an unknown kind is a no-op.
 */
export function prefetchDrillChunks(
  kind: StepTypeCamelCase,
  slots: readonly PrefetchSlot[] = ['drill', 'solve', 'info'],
): Promise<void> {
  const isDrill = Object.hasOwn(LOADERS, kind);
  if (!isDrill && kind !== 'Theory' && kind !== 'Filter') {
    return Promise.resolve();
  }
  const jobs: Promise<void>[] = [];
  for (const slot of slots) {
    if (slot === 'info') {
      const key = kind === 'Theory' ? 'Theory' : 'default';
      jobs.push(prefetchOnce(`info:${key}`, INFO_LOADERS[key]));
    } else if (isDrill) {
      const k = kind as DrillKind;
      jobs.push(prefetchOnce(`${k}:${slot}`, LOADERS[k][slot]));
    }
  }
  return Promise.all(jobs).then(() => undefined);
}

/**
 * @param kind the RESOLVED drill kind (a Filter step as its real kind); null → no cards.
 * @param stepKind the raw step kind: Theory gets its own info card, any other the default.
 */
export function useDrillComponents(
  kind: MaybeRefOrGetter<DrillKind | null | undefined>,
  stepKind: MaybeRefOrGetter<StepTypeCamelCase | null | undefined>,
) {
  function slotComponent(slot: DrillSlot) {
    return computed<Component | null>(() => {
      const k = toValue(kind);
      return k ? cached(`${k}:${slot}`, LOADERS[k][slot]) : null;
    });
  }

  const infoComponent = computed<Component | null>(() => {
    const s = toValue(stepKind);
    if (!s) return null;
    const key = s === 'Theory' ? 'Theory' : 'default';
    return cached(`info:${key}`, INFO_LOADERS[key]);
  });

  return {
    drillComponent: slotComponent('drill'),
    solveComponent: slotComponent('solve'),
    resultsComponent: slotComponent('results'),
    infoComponent,
  };
}
